/**
 * What a PLAYER is told when the server refuses.
 *
 * THE DEFECT THIS EXISTS FOR, reported 2026-08-25. Buying from a shared Moon
 * Exchange listing failed and the panel rendered this, in the game, at the
 * player:
 *
 *   [Request ID: 565be7701ac1efa6] Server Error Uncaught ConvexError:
 *   {"code":"exchange_locked","finished":0,"required":10} at handler
 *   (../convex/moonExchange.ts:601:16)
 *
 * A request id, a server file path and a line number. The line responsible was
 * `err?.message || <fallback>`, and it appeared at SEVEN player-facing call
 * sites across the Exchange and the card packs — every one of them one server
 * throw away from printing the same thing.
 *
 * `err.message` has no reading in which it is the right thing to show:
 *
 *   - in DEV it is the full internal text, which is the leak above;
 *   - in PRODUCTION Convex redacts every non-`ConvexError` to a bare
 *     "Server Error" and drops ad-hoc properties, so the same expression
 *     degrades to a string that tells the player nothing at all.
 *
 * `ConvexError.data` is the one channel production does not redact. So the rule
 * is: the server structures anything the player must be told as a code, this
 * translates the code, and everything else falls back to copy the caller wrote.
 * A thrown message is never shown.
 *
 * WHAT THIS IS NOT. It does not swallow the failure. Every caller still renders
 * an error state — the refusal stays visible and the action is not reported as
 * having worked. This only decides the WORDS.
 */

import { i18n } from '../i18n.js';

function uiText(key, fallback, vars) {
  try {
    const value = i18n?.t?.(key, vars);
    if (typeof value === 'string' && value && value !== key) return value;
  } catch (_) { /* copy must never break an error path */ }
  const text = typeof fallback === 'string' ? fallback : key;
  if (!vars) return text;
  return text.replace(/\{\{(\w+)\}\}/g, (whole, name) => (
    Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : whole
  ));
}

/**
 * The structured payload, if the server sent one.
 *
 * `err.data` for a real `ConvexError`; the object itself when a caller has
 * already unwrapped it. Deliberately NOT `instanceof ConvexError` — an
 * esbuild-bundled `convex/values` is a different class object and the check
 * fails with "Expected constructor: ConvexError, Received constructor:
 * ConvexError".
 */
function structured(error) {
  const data = error?.data ?? error;
  return data && typeof data === 'object' ? data : null;
}

/** The server's code for this refusal, or '' when it did not send one. */
export function errorCode(error) {
  const code = structured(error)?.code;
  return typeof code === 'string' ? code : '';
}

/**
 * The Moon Exchange lock, decoded.
 *
 * Returns `{ finished, required }` or null. The gate binds BOTH buying and
 * selling and refuses server-side, so this is read on the failure path and,
 * better, ahead of it — a disabled button that explains the rule beats a
 * refusal after the player has committed to the press.
 */
export function exchangeLock(error) {
  if (errorCode(error) !== 'exchange_locked') return null;
  const data = structured(error);
  return {
    finished: Number(data.finished) || 0,
    required: Number(data.required) || 0,
  };
}

/** The sentence for a locked Exchange, wherever it is shown. */
export function exchangeLockMessage({ finished, required }) {
  return uiText(
    'collection.exchange.locked',
    'The Moon Exchange opens after you win {{required}} matches. You have won {{finished}}.',
    { finished, required },
  );
}

/**
 * Copy for anything thrown, safe to render.
 *
 * @param {unknown} error whatever was caught
 * @param {string} fallback the caller's own sentence for "this did not work"
 * @returns {string} never the thrown message
 *
 * Extending this is deliberately a decision, not a reflex: a code only belongs
 * here once someone has WRITTEN the sentence for it. Codes with no designed
 * copy fall back rather than having a message invented for them at the point
 * of failure.
 */
/**
 * THE VOUCHER REFUSALS, each with a sentence written for it. The server's
 * `vouchers:redeem` answers most of these as a value (`{ redeemed: false,
 * code }`) rather than a throw, so callers pass either the thrown error or that
 * result — `errorCode` reads `.code` off both shapes.
 *
 * Written as literal `uiText` calls, not a table, so the translation parity
 * sweep sees every key.
 */
function voucherRefusalCopy(code) {
  switch (code) {
    case 'voucher_invalid':
      return uiText('voucher.error.invalid', 'That code is not valid.');
    case 'voucher_not_started':
      return uiText('voucher.error.notStarted', 'This code is not active yet.');
    case 'voucher_expired':
      return uiText('voucher.error.expired', 'This code has expired.');
    case 'voucher_exhausted':
      return uiText('voucher.error.exhausted', 'This code has already been fully claimed.');
    case 'voucher_already_redeemed':
      return uiText('voucher.error.alreadyRedeemed', 'You have already redeemed this code.');
    case 'voucher_pass_conflict':
      return uiText('voucher.error.passConflict', 'You already hold a pass, so this code cannot add another.');
    case 'voucher_comp_cap_reached':
      return uiText('voucher.error.compCapReached', 'All the passes this code could grant have been claimed.');
    case 'voucher_too_many_attempts':
      return uiText('voucher.error.tooManyAttempts', 'Too many tries. Wait a little and try again.');
    case 'voucher_unavailable':
      return uiText('voucher.error.unavailable', 'Code redemption is paused right now. Try again later.');
    case 'upgrade_processing':
      return uiText('voucher.error.upgradeProcessing', 'Your upgrade payment is still being processed. Finish it before claiming a pass gift.');
    case 'gift_processing':
      return uiText('voucher.error.giftProcessing', 'Your previous gift is still updating billing. Wait a moment and try again.');
    case 'gift_game_in_progress':
      return uiText('voucher.error.gameInProgress', 'Finish your current match before claiming this gift.');
    case 'gift_would_shorten_paid_service':
      return uiText('voucher.error.wouldShortenPaidService', 'This gift would end before your prepaid service. Keep it and claim it later.');
    case 'account_banned':
      return uiText('voucher.error.banned', 'This account cannot redeem codes.');
    case 'authentication_required':
      return uiText('voucher.error.signIn', 'Sign in again to redeem a code.');
    default:
      return null;
  }
}

export function playerFacingError(error, fallback) {
  const lock = exchangeLock(error);
  if (lock) return exchangeLockMessage(lock);
  /* On hold for a disputed payment (2026-09-28): the Exchange and the packs
     refuse every Stardust spend until the bank decides. */
  if (errorCode(error) === 'payment_dispute_open') {
    return uiText(
      'accountOnHold.stardustLine',
      'Stardust spending is on hold while your bank reviews a disputed payment on this account. No Stardust was spent.',
    );
  }
  // Card packs are off in some countries (Brazil); the server refuses there.
  if (errorCode(error) === 'packs_unavailable_in_region') {
    return uiText('collection.packs.unavailableInRegion', 'Card packs aren\'t available in your country.');
  }
  const voucher = voucherRefusalCopy(errorCode(error));
  if (voucher) return voucher;
  return fallback;
}
